/**
 * RSA/AES 加密工具
 * 基于 Web Crypto API 实现
 *
 * 加密流程：
 * 1. 生成随机 32 字节 AES-256 密钥
 * 2. 用 RSA-2048-OAEP (SHA-256) 加密 AES 密钥
 * 3. 用 AES-256-GCM 加密请求体
 */

import { RSA_PUBLIC_KEY } from '@/constants/rsa'

/**
 * 检测 Web Crypto API 是否可用（需要安全上下文）
 */
export const isWebCryptoAvailable = (): boolean => {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined'
}

// 缓存导入的 RSA 公钥
let cachedPublicKey: CryptoKey | null = null

/**
 * 将 PEM 格式的公钥转换为 ArrayBuffer
 */
function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s/g, '')

  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * 导入 RSA 公钥
 */
async function importRSAPublicKey(): Promise<CryptoKey> {
  if (cachedPublicKey) {
    return cachedPublicKey
  }

  const keyData = pemToArrayBuffer(RSA_PUBLIC_KEY)

  cachedPublicKey = await crypto.subtle.importKey(
    'spki',
    keyData,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  )

  return cachedPublicKey
}

/**
 * 生成随机 AES-256 密钥
 */
async function generateAESKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  )
}

/**
 * 生成随机 IV (12 字节 for AES-GCM)
 */
function generateIV(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(12))
}

/**
 * 将 ArrayBuffer 转换为 Base64 字符串
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
}

/**
 * 将 Base64 字符串转换为 ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * RSA 加密 AES 密钥
 */
async function rsaEncryptKey(aesKey: CryptoKey, rsaPublicKey: CryptoKey): Promise<string> {
  // 导出 AES 密钥为 raw 格式
  const rawKey = await crypto.subtle.exportKey('raw', aesKey)

  // RSA-OAEP 加密
  const encryptedKey = await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    rsaPublicKey,
    rawKey
  )

  return arrayBufferToBase64(encryptedKey)
}

/**
 * AES-GCM 加密数据
 */
async function aesEncrypt(data: string, aesKey: CryptoKey, iv: Uint8Array): Promise<string> {
  const encoder = new TextEncoder()
  const encodedData = encoder.encode(data)

  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    aesKey,
    encodedData
  )

  return arrayBufferToBase64(encryptedData)
}

/**
 * AES-GCM 解密数据
 */
async function aesDecrypt(encryptedData: string, aesKey: CryptoKey, iv: string): Promise<string> {
  const encryptedBuffer = base64ToArrayBuffer(encryptedData)
  const ivBuffer = base64ToArrayBuffer(iv)

  const decryptedData = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(ivBuffer),
    },
    aesKey,
    encryptedBuffer
  )

  const decoder = new TextDecoder()
  return decoder.decode(decryptedData)
}

/**
 * 生成一份"信封"用的 RSA 加密 AES 密钥 (不加密任何请求体)
 *
 * 给没有请求体、密钥要放请求头的场景用 (如视讯登录凭据 SSE: GET 请求,
 * 密钥放 `X-Encrypted-Key` 头; 见 docs/25-live-frontend-integration.md 第二节)。
 * 返回的 aesKey 用于后续 decryptResponse() 解密该连接收到的每一条消息。
 */
export async function generateEncryptionEnvelope(): Promise<{
  encryptedKey: string
  aesKey: CryptoKey
}> {
  const rsaPublicKey = await importRSAPublicKey()
  const aesKey = await generateAESKey()
  const encryptedKey = await rsaEncryptKey(aesKey, rsaPublicKey)
  return { encryptedKey, aesKey }
}

/**
 * 加密请求数据
 * @param data 请求数据对象
 * @returns 加密后的请求包裹
 */
export async function encryptRequest<T extends object>(
  data: T
): Promise<{
  encryptedKey: string
  encryptedData: string
  iv: string
  aesKey: CryptoKey // 保留用于解密响应
}> {
  const rsaPublicKey = await importRSAPublicKey()
  const aesKey = await generateAESKey()
  const iv = generateIV()

  const jsonData = JSON.stringify(data)

  const encryptedKey = await rsaEncryptKey(aesKey, rsaPublicKey)
  const encryptedData = await aesEncrypt(jsonData, aesKey, iv)
  const ivBase64 = arrayBufferToBase64(iv.buffer)

  return {
    encryptedKey,
    encryptedData,
    iv: ivBase64,
    aesKey,
  }
}

/**
 * 解密响应数据
 * @param encryptedData 加密的响应数据
 * @param iv IV
 * @param aesKey 用于解密的 AES 密钥（与请求时相同）
 * @returns 解密后的数据
 */
export async function decryptResponse<T>(encryptedData: string, iv: string, aesKey: CryptoKey): Promise<T> {
  const decryptedJson = await aesDecrypt(encryptedData, aesKey, iv)
  return JSON.parse(decryptedJson) as T
}

/**
 * 使用 crypto.getRandomValues 生成 UUID v4
 */
function generateUUIDFallback(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * 生成 UUID v4（惰性函数，首次调用后确定实现）
 * 兼容非 HTTPS 环境（内网 HTTP）
 */
export let generateUUID = (): string => {
  // 首次调用时判断并重写函数
  if (typeof crypto.randomUUID === 'function') {
    try {
      const uuid = crypto.randomUUID()
      // 原生 API 可用，重写为原生实现
      generateUUID = () => crypto.randomUUID()
      return uuid
    } catch {
      // 非安全上下文，使用备用方案
    }
  }
  // 重写为备用实现
  generateUUID = generateUUIDFallback
  return generateUUIDFallback()
}

/**
 * 获取当前 Unix 时间戳（秒）
 */
export function getTimestamp(): number {
  return Math.floor(Date.now() / 1000)
}
